import type { LineItem, Receivable, ReceivableDraft } from "./types";

/**
 * The editor's starting state for a SAVED draft (FIN-829 F3) — unlike
 * `baseOnExistingInvoice`, which builds a new draft, this keeps the draft's own identity
 * (id, number, invoice date) and every saved field, so saving writes back into the same
 * receivable (AC3/AC4).
 *
 * Lines keep their stored rate. A line that carries its `vatCategory` is re-derived only
 * when the customer or description later changes; a line saved before categories were
 * stored has none, and is left for the editor to classify, never silently reset to 25%.
 */
export function draftForEditing(source: Receivable): ReceivableDraft {
  return {
    id: source.id,
    number: source.number,
    status: source.status,
    customerId: source.customerId,
    invoiceDate: source.invoiceDate,
    paymentTermsDays: source.paymentTermsDays,
    lines: source.lines.map((l) => ({ ...l })),
    ourReference: source.ourReference,
    theirReference: source.theirReference,
    recurrence: source.recurrence,
    createdVia: source.createdVia,
    sourceInvoiceId: source.sourceInvoiceId,
    interestRatePercent: source.interestRatePercent,
    freeText: source.freeText,
    paymentMethod: source.paymentMethod,
    receivingBankAccountId: source.receivingBankAccountId,
    documentLanguage: source.documentLanguage,
    currency: source.currency,
  };
}

/**
 * The lines a save sends, in both apps: blank lines are dropped, and a line's VAT is left to
 * the backend engine unless the user deliberately overrode it (FIN-603 F6).
 */
export function linesToSave(lines: LineItem[]) {
  return lines
    .filter((l) => l.description.trim() && l.quantity * l.unitPrice > 0)
    .map((l) => ({
      articleId: l.articleId,
      description: l.description,
      quantity: l.quantity,
      unit: l.unit,
      unitPrice: l.unitPrice,
      vatRateOverride: l.vatManual ? l.vatRate : undefined,
      vatRateManual: l.vatManual ?? false,
    }));
}

/**
 * What a sender can change on a draft, for telling an edited draft from the one loaded
 * (FIN-829 EC10). Engine-derived line fields are left out: a reclassification is not an edit.
 */
export function draftEditSignature(d: ReceivableDraft): string {
  return JSON.stringify({
    customerId: d.customerId, invoiceDate: d.invoiceDate, paymentTermsDays: d.paymentTermsDays,
    ourReference: d.ourReference, theirReference: d.theirReference, interestRatePercent: d.interestRatePercent,
    freeText: d.freeText, paymentMethod: d.paymentMethod, receivingBankAccountId: d.receivingBankAccountId,
    documentLanguage: d.documentLanguage, currency: d.currency,
    lines: d.lines.map((l) => [l.description, l.quantity, l.unit, l.unitPrice, l.vatManual ? l.vatRate : null]),
  });
}
